import { EditorContent } from '@tiptap/react';
import { cn } from '@/shared/lib';
import type { RichTextEditorProps } from '@/shared/ui/rich-text-editor/types';
import {
  richTextEditorStyles,
  richTextProseStyles,
} from '@/shared/ui/rich-text-editor/styles';
import { Toolbar } from './toolbar';
import { useControlledEditor } from './use-controlled-editor';

const DEFAULT_MIN_HEIGHT = 120;
const DEFAULT_MAX_HEIGHT = 480;

/**
 * The controlled editing core: one value in, one value out.
 *
 * The chrome is house primitives — the surface, its invalid/disabled states and
 * the toolbar — while Tiptap owns only the document and the contentEditable.
 * Read-only and disabled are separate states that reach the same toolbar answer:
 * no control may issue a command the surface would refuse.
 */
export function RichTextEditor({
  value,
  onChange,
  label,
  isDisabled = false,
  isReadOnly = false,
  isInvalid = false,
  errorMessage,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  renderImageSource,
}: RichTextEditorProps) {
  const { editor, errorId } = useControlledEditor({
    value,
    onChange,
    label,
    isDisabled,
    isReadOnly,
    isInvalid,
    errorMessage,
  });

  return (
    <div className={richTextEditorStyles.root}>
      {editor ? (
        <Toolbar
          editor={editor}
          isDisabled={isDisabled || isReadOnly}
          renderImageSource={renderImageSource}
        />
      ) : null}
      <div
        className={cn(
          richTextEditorStyles.surface,
          isInvalid && richTextEditorStyles.surfaceInvalid,
          isDisabled && richTextEditorStyles.surfaceDisabled,
        )}
      >
        <EditorContent
          editor={editor}
          className={cn(richTextProseStyles, richTextEditorStyles.content, 'overflow-y-auto')}
          style={{ minHeight, maxHeight }}
        />
      </div>
      {errorMessage ? (
        // The same surface as `Input`: a plain paragraph the control is
        // described by, with the house error text. Not a live region.
        <p id={errorId} className={richTextEditorStyles.error}>
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
